import { extractMergeTags } from "../../../../../../../utils/extract-merge-tags";
import { isEmailChannel } from "../../../../../../channels/types";
import type { SerialisedLexicalEditorDesign } from "../../../../../../message/editor/types/serialised-editor-design";
import type { MomentFormMoment } from "../../../../../types/moment-form-moment";
import { usePreviewContext } from "../preview-context";

export type EmailPreviewSourceInput = Pick<
  MomentFormMoment,
  "channel" | "use_lexical_editor" | "design" | "email_subject" | "from"
> & {
  email_moment?: Pick<
    NonNullable<MomentFormMoment["email_moment"]>,
    "subject" | "from_address" | "body"
  > | null;
};

export type EmailPreviewSource = {
  channel: MomentFormMoment["channel"];
  design: SerialisedLexicalEditorDesign | null;
  subject: string | null;
  from: string | null;
  additional_merge_fields: string[];
  use_unlayer_email_preview: boolean;
};

export function getEmailPreviewSource({
  channel,
  use_lexical_editor,
  design,
  email_subject,
  from,
  email_moment,
}: EmailPreviewSourceInput): EmailPreviewSource {
  const use_unlayer_email_preview = isEmailChannel(channel) && use_lexical_editor === false;
  const lexicalDesign = design && design.type !== "markdown" ? design : null;

  return {
    channel,
    design: use_unlayer_email_preview ? null : lexicalDesign,
    subject: use_unlayer_email_preview ? (email_moment?.subject ?? null) : (email_subject ?? null),
    from: use_unlayer_email_preview ? (email_moment?.from_address ?? null) : (from ?? null),
    additional_merge_fields: use_unlayer_email_preview
      ? extractMergeTags([email_moment?.body ?? ""])
      : [],
    use_unlayer_email_preview,
  };
}

export function useEmailPreviewSource(): EmailPreviewSource {
  const {
    moment_preview_data: {
      channel,
      use_lexical_editor,
      design: inputDesign,
      email_subject,
      from,
      email_moment,
    },
  } = usePreviewContext();

  return getEmailPreviewSource({
    channel,
    use_lexical_editor: use_lexical_editor ?? false,
    design: inputDesign,
    email_subject,
    from,
    email_moment: email_moment
      ? {
          subject: email_moment.subject ?? "",
          from_address: email_moment.from_address ?? "",
          body: email_moment.body ?? "",
        }
      : null,
  });
}
